import { daysBetween } from './dates'

/**
 * Where the household says what its figures are counted in, and what they are
 * counted in until it does.
 *
 * Stated once here because the valuation job, the pricing job and the deep
 * backfill each answered this for themselves, and three places falling back to
 * the same literal is not the same as them agreeing — it is three chances to
 * stop agreeing, silently, in a product where the disagreement would read as a
 * number rather than as a fault.
 */
export const BASE_CURRENCY_SETTING_KEY = 'baseCurrency'

export const DEFAULT_BASE_CURRENCY = 'DKK'

/**
 * ISO 4217 as the runtime's own table states it, rather than a list kept here.
 * A list in this file would be a second copy of a public fact, and the copy is
 * always the one that goes out of date.
 */
const CURRENCY_CODES: ReadonlySet<string> = new Set(Intl.supportedValuesOf('currency'))

/**
 * Three letters is not the test. The valuation only recognises a code it can
 * find a rate for and falls back to the default for anything else, so a code
 * that is merely the right length would reprice every figure on the page
 * against nothing and report no fault while doing it.
 */
export function isCurrencyCode(value: string): boolean {
  return CURRENCY_CODES.has(value.toUpperCase())
}

/**
 * The longest gap the ECB's own calendar leaves between two publications: it
 * quotes on TARGET business days, and Good Friday with Easter Monday, or
 * Christmas with New Year, turn a weekend into four days without a quote. A
 * rate that old is the rate in force rather than a stand-in for one, and
 * marking it would put a caveat on every weekend until nobody read them.
 */
export const FX_PUBLICATION_GAP_DAYS = 4

/**
 * Past this the rate is standing in for days the ECB did publish on and this
 * product failed to collect, which is a guess about a market that has moved
 * without it. A week bounds the guess at about a week of currency drift, and
 * leaves a run of bad nights survivable without anyone being woken. Beyond it
 * the honest answer is that the pair has no rate, which the product already
 * reports as such rather than counting the holding as nothing.
 */
export const FX_CARRY_LIMIT_DAYS = 7

/** What a rate observed on one day can still be said to be on a later one. */
export type FxStanding = 'in_force' | 'carried' | 'expired'

export function fxStanding(observedOn: string, on: string): FxStanding {
  const age = daysBetween(observedOn, on)
  if (age <= FX_PUBLICATION_GAP_DAYS) return 'in_force'
  return age <= FX_CARRY_LIMIT_DAYS ? 'carried' : 'expired'
}

/**
 * A rate with no publication day recorded is one written before rates carried
 * theirs. Reading it as observed on its own date is what the product believed
 * about every rate until now, so the upgrade changes no figure by itself.
 */
export function observedOnOf(row: { date: string; observedOn?: string | null }): string {
  return row.observedOn ?? row.date
}
